import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { Skeleton, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { type PlacementCandidate } from "../../../PlacementCandidate/types";
import { useUpdateInterview, type WorkerOutcomeResult } from "../../api/useUpdateInterview";
import {
  transformPaginatedInterviews,
  useInterviewAttendanceOutcome,
} from "../../hooks/useInterviewAttendanceOutcome";
import { PLACEMENT_PATHS } from "../../paths";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";
import { InterviewAttendanceFeedbackStepContent } from "./InterviewAttendanceFeedbackStepContent";
import { type FreeTextContext } from "./optionConstants";
import { useInterviewAttendanceSource } from "./useInterviewAttendanceSource";

const STEPS_WITH_BACK_BUTTON = new Set<FeedbackStep>([
  FEEDBACK_STEP.workplace,
  FEEDBACK_STEP.notInterview,
  FEEDBACK_STEP.notOfferTheRole,
  FEEDBACK_STEP.gotOfferSubOptions,
  FEEDBACK_STEP.notHeardBackSubOptions,
  FEEDBACK_STEP.freeText,
]);

interface InterviewAttendanceFeedbackDialogProps {
  placementCandidate: PlacementCandidate;
  modalState: UseModalState;
  onClose?: () => void;
}

export function InterviewAttendanceFeedbackDialog(props: InterviewAttendanceFeedbackDialogProps) {
  const { placementCandidate, modalState, onClose } = props;
  const source = useInterviewAttendanceSource();
  const worker = useDefinedWorker();
  const history = useHistory();

  const {
    isLoading: isFetchingInterviews,
    isSuccess: isGetInterviewsSuccess,
    data: interviewsDataWithPages,
    fetchNextPage: fetchNextPageInterviews,
    refetch: refetchInterviews,
    hasNextPage: hasNextPageInterview,
    isFetchingNextPage: isFetchingInterviewsNextPage,
  } = useInterviewAttendanceOutcome(
    {
      workerId: placementCandidate.worker.id,
      withDismissed: false,
    },
    { enabled: modalState.modalIsOpen }
  );
  const { mutateAsync: updateInterview } = useUpdateInterview();
  const [selectedInterview, setSelectedInterview] = useState<InterviewWithDetails>();
  const [feedbackStep, setFeedbackStep] = useState<FeedbackStep>();
  const [freeTextContext, setFreeTextContext] = useState<FreeTextContext>();

  const interviews = transformPaginatedInterviews(interviewsDataWithPages?.pages, worker.tmz ?? "");
  const activeInterview =
    selectedInterview ?? (interviews?.length === 1 ? interviews[0] : undefined);
  const hasPendingInterview = interviews && interviews.length > 0;

  useLogEffect(
    APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_VIEWED,
    {
      placementCandidate,
      interviews,
      ...(interviews && { interviewsCount: interviews.length }),
      source,
    },
    { enabled: isGetInterviewsSuccess }
  );

  function handleCtaClicked(toJobsPath?: boolean) {
    if (interviews?.length === 1) {
      setSelectedInterview(interviews[0]);
      setFeedbackStep(FEEDBACK_STEP.workplace);
    } else if (interviews && interviews.length > 1) {
      setSelectedInterview(undefined);
      setFeedbackStep(FEEDBACK_STEP.workplaces);
    } else {
      modalState.closeModal();
      if (toJobsPath) {
        history.push(PLACEMENT_PATHS.ROOT);
      }
    }
  }

  function handleFreeTextNeeded(
    parentStep: FeedbackStep,
    confirmationStep: FeedbackStep,
    result: WorkerOutcomeResult,
    textFieldConfig: { heading: string; placeholder?: string }
  ) {
    setFreeTextContext({
      result,
      textFieldHeading: textFieldConfig.heading,
      textFieldPlaceholder: textFieldConfig.placeholder,
      parentStep,
      confirmationStep,
    });
    setFeedbackStep(FEEDBACK_STEP.freeText);
  }

  function handleBackNavigation() {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (feedbackStep) {
      case "not-interview":
      case "not-offer-the-role":
      case "got-offer-sub-options":
      case "not-heard-back-sub-options": {
        setFeedbackStep("workplace");
        break;
      }

      case "free-text": {
        if (freeTextContext) {
          setFeedbackStep(freeTextContext.parentStep);
          setFreeTextContext(undefined);
        }

        break;
      }

      default: {
        setSelectedInterview(undefined);
      }
    }
  }

  return (
    <BottomSheet canDismiss={false} modalState={modalState}>
      <Stack sx={{ p: 5, py: 6 }}>
        <Stack justifyContent="space-between" direction="row-reverse">
          <IconButton
            size="small"
            iconType="close"
            variant="outlined"
            onClick={async () => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_ACTIONS, {
                action: "close",
                step: feedbackStep,
                placementCandidate,
                interview: activeInterview,
                interviews,
                ...(interviews && { interviewsCount: interviews.length }),
                source,
              });
              onClose?.();
              modalState.closeModal();
              if (interviews) {
                await Promise.all(
                  interviews.map(async (interview) => {
                    await updateInterview({
                      interviewId: interview.id,
                      attributes: { workerOutcome: { dismissedAt: new Date() } },
                    });
                  })
                );
              }
            }}
          />
          {activeInterview && feedbackStep && STEPS_WITH_BACK_BUTTON.has(feedbackStep) ? (
            <IconButton
              size="small"
              iconType="chevron-left"
              variant="outlined"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_FEEDBACK_DIALOG_ACTIONS, {
                  action: "back",
                  step: feedbackStep,
                  placementCandidate,
                  interview: activeInterview,
                  interviews,
                  source,
                });
                handleBackNavigation();
              }}
            />
          ) : undefined}
        </Stack>
        {isFetchingInterviews ? (
          <Stack
            spacing={8}
            alignItems="center"
            justifyContent="center"
            sx={{ paddingX: 8, paddingY: 6 }}
          >
            <Stack alignItems="center" spacing={8}>
              <Skeleton variant="circular" width={98} height={98} />
              <Stack spacing={0} alignItems="center">
                <Skeleton width={256} height={64} />
                <Skeleton width={360} height={32} />
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <InterviewAttendanceFeedbackStepContent
            feedbackStep={feedbackStep}
            activeInterview={activeInterview}
            interviews={interviews}
            placementCandidate={placementCandidate}
            freeTextContext={freeTextContext}
            hasPendingInterview={hasPendingInterview}
            isFetchingInterviews={isFetchingInterviews}
            isGetInterviewsSuccess={isGetInterviewsSuccess}
            isFetchingInterviewsNextPage={isFetchingInterviewsNextPage}
            hasNextPageInterview={hasNextPageInterview}
            refetchInterviews={refetchInterviews}
            fetchNextPageInterviews={fetchNextPageInterviews}
            onCtaClicked={(toJobsPath) => {
              handleCtaClicked(toJobsPath);
            }}
            onFreeTextNeeded={(parentStep, confirmationStep, result, textFieldConfig) => {
              handleFreeTextNeeded(parentStep, confirmationStep, result, textFieldConfig);
            }}
            onFeedbackStepChange={(step) => {
              setFeedbackStep(step);
            }}
            onFreeTextContextClear={() => {
              setFreeTextContext(undefined);
            }}
            onInterviewSelected={(interview) => {
              setFeedbackStep(FEEDBACK_STEP.workplace);
              setSelectedInterview(interview);
            }}
            onAllSetDismiss={() => {
              modalState.closeModal();
              history.push(PLACEMENT_PATHS.ROOT);
            }}
          />
        )}
      </Stack>
    </BottomSheet>
  );
}
